import {useMutation, useQueryClient} from "react-query";
import Alert from "react-s-alert";

import {http} from "../../../../../services/api/restApiClient";
import {
  WorkflowTriggerModel,
  WorkflowTriggerPatchRequest,
  WorkflowTriggerRequest,
} from "../types";

const workflowTriggersUrl = (facilityId: string) =>
  `/facilities/${facilityId}/workflow-triggers`;

const invalidateAndNotify = (
  queryClient: ReturnType<typeof useQueryClient>,
  facilityId: string,
  message: string,
) => {
  queryClient.invalidateQueries(["workflowTriggers", facilityId]);
  Alert.success(message);
};

const notifyError = (error: any, fallbackMessage: string) => {
  Alert.error(error?.data?.detail || error?.data?.message || fallbackMessage);
};

export const useCreateWorkflowTriggerMutation = () => {
  const queryClient = useQueryClient();

  return useMutation(
    (params: {facilityId: string; trigger: WorkflowTriggerRequest}) =>
      http.post<WorkflowTriggerModel>(
        workflowTriggersUrl(params.facilityId),
        params.trigger,
      ),
    {
      onSuccess: (_data, {facilityId}) =>
        invalidateAndNotify(
          queryClient,
          facilityId,
          "Workflow trigger created.",
        ),
      onError: (error: any) =>
        notifyError(error, "Failed to create workflow trigger."),
    },
  );
};

export const useUpdateWorkflowTriggerMutation = () => {
  const queryClient = useQueryClient();

  return useMutation(
    (params: {
      facilityId: string;
      triggerId: string;
      trigger: WorkflowTriggerPatchRequest;
    }) =>
      http.patch<WorkflowTriggerModel>(
        `${workflowTriggersUrl(params.facilityId)}/${params.triggerId}`,
        params.trigger,
      ),
    {
      onSuccess: (_data, {facilityId}) =>
        invalidateAndNotify(
          queryClient,
          facilityId,
          "Workflow trigger updated.",
        ),
      onError: (error: any) =>
        notifyError(error, "Failed to update workflow trigger."),
    },
  );
};

export const useDeleteWorkflowTriggerMutation = () => {
  const queryClient = useQueryClient();

  return useMutation(
    (params: {facilityId: string; triggerId: string}) =>
      http.delete(
        `${workflowTriggersUrl(params.facilityId)}/${params.triggerId}`,
      ),
    {
      onSuccess: (_data, {facilityId}) =>
        invalidateAndNotify(
          queryClient,
          facilityId,
          "Workflow trigger deleted.",
        ),
      onError: (error: any) =>
        notifyError(error, "Failed to delete workflow trigger."),
    },
  );
};
